import styles from './product-updates.module.css'

import cn from 'clsx'
import React from 'react'

import mixpanel from '@disco/api/mixpanel'
import { DISCO_CHANGELOG_URL } from '@disco/constants/external-links'
import { getRelativeTime } from '@disco/utils/date'

import { Flex, Link, Spinner, Text } from 'components/ds'
import { ExternalLinkIcon } from 'components/ui/icon/svg-icons'

import type { ProductUpdate } from './use-product-updates'
import { useProductUpdates } from './use-product-updates'

const ExternalLink = ({
  href,
  ariaLabel,
  onClick,
  children,
}: {
  href: string
  ariaLabel?: string
  onClick: () => void
  children: React.ReactNode
}) => (
  <Link
    href={href}
    aria-label={ariaLabel}
    onClick={onClick}
    target="_blank"
    rel="noopener noreferrer"
    size="2"
    weight="medium"
    className={styles.externalLink}
  >
    {children}
    <ExternalLinkIcon size={14} />
  </Link>
)

// Mirrors the badge colours on disco.ac/changelog; anything else (e.g. Fix) stays grey
const CATEGORY_BADGE_CLASSES: Record<string, string> = {
  Feature: styles.badgeFeature,
  Update: styles.badgeUpdate,
  Announcement: styles.badgeAnnouncement,
}

const CategoryBadge = ({ category }: { category: string }) => (
  <Text
    size="1"
    weight="bold"
    className={cn(styles.badge, CATEGORY_BADGE_CLASSES[category])}
  >
    {category}
  </Text>
)

const trackLinkClick = (props: Record<string, unknown>) =>
  mixpanel.sendPureEvent('Access product update via notifications', {
    Context: 'Notifications',
    ...props,
  })

const ProductUpdateItem = ({
  update,
  position,
}: {
  update: ProductUpdate
  position: number
}) => (
  <Flex direction="column" gap="1" py="3" className={styles.item}>
    {Boolean(update.category || update.publishedDate) && (
      <Flex align="center" gap="2">
        {update.category && <CategoryBadge category={update.category} />}
        {Boolean(update.publishedDate) && (
          <Text size="1" color="gray">
            {getRelativeTime(update.publishedDate)}
          </Text>
        )}
      </Flex>
    )}
    <Text as="p" size="2" weight="bold">
      {update.title}
    </Text>
    {update.excerpt && (
      <Text as="p" size="2" color="gray" className={styles.excerpt}>
        {update.excerpt}
      </Text>
    )}
    <ExternalLink
      href={update.url}
      ariaLabel={`Learn more about ${update.title}`}
      onClick={() =>
        trackLinkClick({
          link: 'entry',
          entry_id: update.id,
          entry_title: update.title,
          category: update.category,
          position,
        })
      }
    >
      Learn more
    </ExternalLink>
  </Flex>
)

const ProductUpdatesBody = () => {
  const { updates, status } = useProductUpdates()

  if (status === 'loading') {
    return (
      <Flex justify="center" p="5">
        <Spinner />
      </Flex>
    )
  }

  if (status === 'error' || updates.length === 0) {
    return (
      <Flex justify="center" p="5">
        <Text size="2">
          {status === 'error'
            ? 'Couldn’t load product updates'
            : 'No product updates yet'}
        </Text>
      </Flex>
    )
  }

  return (
    <>
      {updates.map((update, position) => (
        <ProductUpdateItem
          key={update.id}
          update={update}
          position={position}
        />
      ))}
    </>
  )
}

/** Latest DISCO changelog entries, shown in the bell popover. */
const ProductUpdates = () => (
  <Flex direction="column">
    <div className={styles.list}>
      <ProductUpdatesBody />
    </div>
    <Flex justify="center" pt="3" className={styles.footer}>
      <ExternalLink
        href={DISCO_CHANGELOG_URL}
        onClick={() => trackLinkClick({ link: 'view_all' })}
      >
        View all product updates
      </ExternalLink>
    </Flex>
  </Flex>
)

export default ProductUpdates
